<template>
  <div class="member-hours">
    <p class="member-hours__intro">
      Standaard geldt de werktijd van de organisatie (
      <strong>{{ orgWorkTime.workday_start }}–{{ orgWorkTime.workday_end }}</strong>
      ). Hier wijk je daar per dag van af voor {{ memberName }}.
    </p>

    <ToggleInput
      id="member-hours-schedulable"
      v-model="form.schedulable"
      label="Inplanbaar in planning"
    />
    <p class="member-hours__hint">
      Uitgeschakeld verdwijnt deze persoon als kolom uit de planning en uit de monteurkeuze.
    </p>

    <div class="member-hours__days" :class="{ 'member-hours__days--muted': !form.schedulable }">
      <div
        v-for="key in WEEKDAY_KEYS"
        :key="key"
        class="member-hours__day"
        :class="{ 'member-hours__day--off': form.days[key].mode === 'off' }"
      >
        <span class="member-hours__day-label">{{ WEEKDAY_LABELS[key] }}</span>

        <select
          class="member-hours__mode"
          :value="form.days[key].mode"
          @change="setMode(key, $event.target.value)"
        >
          <option value="org">Standaard</option>
          <option value="custom">Eigen tijden</option>
          <option value="off">Vrij</option>
        </select>

        <div v-if="form.days[key].mode === 'custom'" class="member-hours__range">
          <TimeInput :id="`member-hours-${key}-start`" v-model="form.days[key].start" size="sm" />
          <span class="member-hours__dash">–</span>
          <TimeInput :id="`member-hours-${key}-end`" v-model="form.days[key].end" size="sm" />
        </div>
        <span v-else-if="form.days[key].mode === 'org'" class="member-hours__org">
          {{ orgWorkTime.workday_start }}–{{ orgWorkTime.workday_end }}
        </span>
        <span v-else class="member-hours__org">—</span>

        <span v-if="errors[key]" class="member-hours__error">{{ errors[key] }}</span>
      </div>
    </div>

    <div class="member-hours__lunch">
      <span class="member-hours__day-label">Lunchpauze</span>
      <select
        class="member-hours__mode"
        :value="form.lunch.mode"
        @change="setLunchMode($event.target.value)"
      >
        <option value="org">Standaard</option>
        <option value="custom">Eigen tijd</option>
        <option value="none">Geen</option>
      </select>
      <div v-if="form.lunch.mode === 'custom'" class="member-hours__range">
        <TimeInput id="member-hours-lunch-start" v-model="form.lunch.start" size="sm" />
        <span class="member-hours__dash">–</span>
        <TimeInput id="member-hours-lunch-end" v-model="form.lunch.end" size="sm" />
      </div>
      <span v-else-if="form.lunch.mode === 'org'" class="member-hours__org">
        {{
          orgWorkTime.lunch_break
            ? `${orgWorkTime.lunch_break.start}–${orgWorkTime.lunch_break.end}`
            : 'geen'
        }}
      </span>
      <span v-else class="member-hours__org">—</span>
      <span v-if="errors.lunch" class="member-hours__error">{{ errors.lunch }}</span>
    </div>

    <div class="member-hours__footer">
      <Btn variant="plain" size="sm" :disabled="saving" @click="reset">Terug naar standaard</Btn>
      <div class="member-hours__footer-actions">
        <Btn variant="outlined" size="sm" :disabled="saving" @click="onCancel">Annuleren</Btn>
        <Btn variant="primary" size="sm" :loading="saving" @click="submit">Opslaan</Btn>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, reactive, computed } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import TimeInput from '@/components/inputs/TimeInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import { parseHmToMinutes } from '@/utils/planningSlots';
  import {
    WEEKDAY_KEYS,
    WEEKDAY_LABELS,
    getMemberPlanningSettings,
  } from '@/utils/technicianWorkTime';

  export default {
    name: 'MemberWorkHoursModal',
    components: { Btn, TimeInput, ToggleInput },
    props: {
      member: { type: Object, required: true },
      orgWorkTime: { type: Object, required: true },
      onSubmit: { type: Function, default: () => {} },
      onCancel: { type: Function, default: () => {} },
    },
    setup(props) {
      const current = getMemberPlanningSettings(props.member.planning_settings);

      const dayState = (key) => {
        if (!(key in current.work_hours)) {
          return {
            mode: 'org',
            start: props.orgWorkTime.workday_start,
            end: props.orgWorkTime.workday_end,
          };
        }
        const value = current.work_hours[key];
        if (value === null) {
          return {
            mode: 'off',
            start: props.orgWorkTime.workday_start,
            end: props.orgWorkTime.workday_end,
          };
        }
        return { mode: 'custom', start: value.start, end: value.end };
      };

      const lunchState = () => {
        const orgLunch = props.orgWorkTime.lunch_break || { start: '12:30', end: '13:00' };
        if (!('lunch_break' in current)) return { mode: 'org', ...orgLunch };
        if (current.lunch_break === null) return { mode: 'none', ...orgLunch };
        return { mode: 'custom', ...current.lunch_break };
      };

      const form = reactive({
        schedulable: current.schedulable,
        days: Object.fromEntries(WEEKDAY_KEYS.map((key) => [key, dayState(key)])),
        lunch: lunchState(),
      });
      const errors = ref({});
      const saving = ref(false);

      const memberName = computed(() => {
        const u = props.member.user || {};
        return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || 'dit teamlid';
      });

      const setMode = (key, mode) => {
        form.days[key].mode = mode;
        delete errors.value[key];
      };
      const setLunchMode = (mode) => {
        form.lunch.mode = mode;
        delete errors.value.lunch;
      };

      const reset = () => {
        form.schedulable = true;
        for (const key of WEEKDAY_KEYS) {
          form.days[key] = {
            mode: 'org',
            start: props.orgWorkTime.workday_start,
            end: props.orgWorkTime.workday_end,
          };
        }
        form.lunch.mode = 'org';
        errors.value = {};
      };

      const validRange = (range) => {
        const s = parseHmToMinutes(range.start);
        const e = parseHmToMinutes(range.end);
        return s != null && e != null && e > s;
      };

      const buildPayload = () => {
        const nextErrors = {};
        const workHours = {};
        for (const key of WEEKDAY_KEYS) {
          const day = form.days[key];
          if (day.mode === 'off') workHours[key] = null;
          if (day.mode === 'custom') {
            if (!validRange(day)) {
              nextErrors[key] = 'Eindtijd moet na begintijd liggen';
              continue;
            }
            workHours[key] = { start: day.start, end: day.end };
          }
        }
        const payload = { schedulable: form.schedulable, work_hours: workHours };
        if (form.lunch.mode === 'none') payload.lunch_break = null;
        if (form.lunch.mode === 'custom') {
          if (!validRange(form.lunch)) {
            nextErrors.lunch = 'Eindtijd moet na begintijd liggen';
          } else {
            payload.lunch_break = { start: form.lunch.start, end: form.lunch.end };
          }
        }
        errors.value = nextErrors;
        if (Object.keys(nextErrors).length) return null;

        const isDefault =
          payload.schedulable && !Object.keys(workHours).length && !('lunch_break' in payload);
        // `null` wipes the row's overrides; the API will store NULL.
        return isDefault ? null : payload;
      };

      const submit = async () => {
        const payload = buildPayload();
        if (Object.keys(errors.value).length) return;
        saving.value = true;
        try {
          await props.onSubmit(payload);
        } finally {
          saving.value = false;
        }
      };

      return {
        WEEKDAY_KEYS,
        WEEKDAY_LABELS,
        form,
        errors,
        saving,
        memberName,
        setMode,
        setLunchMode,
        reset,
        submit,
      };
    },
  };
</script>

<style scoped lang="scss">
  .member-hours {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;

    &__intro {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__hint {
      margin: -0.5rem 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__days {
      display: flex;
      flex-direction: column;
      border: 1px solid var(--slate-200);
      border-radius: 8px;
      overflow: hidden;

      &--muted {
        opacity: 0.55;
      }
    }

    &__day,
    &__lunch {
      display: grid;
      grid-template-columns: 6.5rem 9rem 1fr;
      align-items: center;
      gap: 0.75rem;
      padding: 0.5rem 0.75rem;
      font-size: 0.8125rem;

      @include sm-down {
        grid-template-columns: 1fr 1fr;
      }
    }

    &__day + &__day {
      border-top: 1px solid var(--slate-100);
    }

    &__day--off &__day-label {
      color: var(--slate-400);
    }

    &__lunch {
      border: 1px solid var(--slate-200);
      border-radius: 8px;
    }

    &__day-label {
      font-weight: 500;
      color: var(--slate-700);
    }

    &__mode {
      padding: 0.3rem 1.75rem 0.3rem 0.6rem;
      border: 1px solid var(--slate-200);
      border-radius: 6px;
      background: var(--slate-50);
      font-size: 0.8125rem;
      color: var(--slate-700);
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.5rem center;
      cursor: pointer;
    }

    &__range {
      display: flex;
      align-items: center;
      gap: 0.375rem;

      :deep(.form-group) {
        margin: 0;
      }
    }

    &__dash {
      color: var(--slate-400);
    }

    &__org {
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
    }

    &__error {
      grid-column: 1 / -1;
      font-size: 0.75rem;
      color: var(--red-600);
    }

    &__footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.5rem;
      padding-top: 0.5rem;
      border-top: 1px solid var(--slate-200);
    }

    &__footer-actions {
      display: flex;
      gap: 0.5rem;
    }
  }
</style>
